import { Box } from "@chakra-ui/react";
import { useOverlay } from "@toss/use-overlay";
import {
  ClickEventType,
  ColorIcon,
  colors,
  customEvent,
  OverlayModal,
} from "goi_common";

import { BaseButton } from "../../components";
import { BaseStack } from "../../layout";
import { baseColors } from "../../tokens";

/** 고이 대표번호. 화장장 번호가 없을 때 폴백한다. */
export const GOI_CALL_NUMBER = "1666-9784";

/**
 * 화장장 번호를 tel: 에 넣을 수 있는 형태로 정규화한다.
 *
 * 화장장 원본 데이터는 e하늘 형태를 유지하는 파일이라
 * `031-329-5900~2`(용인평온의 숲) 같은 대표번호 범위 표기가 섞여 있다.
 * 범위 표기는 앞 번호만 남긴다.
 *
 * @returns 정규화된 번호, 값이 없으면 null
 */
export const normalizeCrematoriumPhone = (
  telephone: string | null | undefined
): string | null => {
  if (!telephone) return null;

  const head = telephone.split("~")[0].trim();
  return head || null;
};

type InquiryTrigger =
  | "ask-booking"
  | "ask-relocation"
  | "ask-visit"
  | "ask-etc"
  | "ask-binso"
  | "ask-cost";

export interface InquiryOption {
  label: string;
  trigger: InquiryTrigger;
  /** goi: 고이 대표번호로, facility: 시설 직통으로 (번호가 없으면 고이로 폴백) */
  target: "goi" | "facility";
  /** true 면 발신 시 새벽 2~7시 고객센터 안내(차단)를 건너뛴다. 임종 직후처럼 새벽에도 연결돼야 하는 문의 */
  urgent?: boolean;
}

/** 화장장: 예약·비용과 기타는 고이, 개장·이장과 고인 확인은 화장장 직통 */
export const CREMATORIUM_INQUIRY_OPTIONS: InquiryOption[] = [
  { label: "화장 예약 및 비용 문의", trigger: "ask-booking", target: "goi" },
  {
    label: "개장·이장 관련 문의",
    trigger: "ask-relocation",
    target: "facility",
  },
  { label: "고인 확인 및 방문 문의", trigger: "ask-visit", target: "facility" },
  { label: "기타 문의", trigger: "ask-etc", target: "goi" },
];

/** 장례식장: 조문 문의만 장례식장 직통, 나머지는 고이 */
export const PARLOR_INQUIRY_OPTIONS: InquiryOption[] = [
  {
    label: "빈소 예약 및 고인 이송 문의",
    trigger: "ask-binso",
    target: "goi",
    urgent: true,
  },
  { label: "장례 비용 문의", trigger: "ask-cost", target: "goi" },
  {
    label: "조문 문의 (위치, 주차, 화환 등)",
    trigger: "ask-visit",
    target: "facility",
  },
  { label: "기타 문의", trigger: "ask-etc", target: "goi" },
];

/** 문의 모달 헤더: 제목 + 닫기. onClose 가 ask-close 이벤트까지 책임진다. */
function InquiryModalHeader({ onClose }: { onClose: () => void }) {
  return (
    <BaseStack
      direction="horizontal"
      width="100%"
      alignItems="flex-start"
      justifyContent="space-between"
      p="24px 24px 0"
    >
      <Box
        className="headline_1_sb"
        color={baseColors.gray_900}
        css={{ wordBreak: "keep-all" }}
      >
        어떤 문의가 필요하신가요?
      </Box>
      <Box
        as="button"
        aria-label="닫기"
        flexShrink={0}
        cursor="pointer"
        lineHeight={0}
        onClick={onClose}
      >
        <ColorIcon name="close" width={24} height={24} color={colors.gray500} />
      </Box>
    </BaseStack>
  );
}

function InquiryModalBody({
  eventPath,
  options,
  facilityPhone,
  onDial,
  onClose,
}: {
  eventPath: ClickEventType["path"];
  options: InquiryOption[];
  facilityPhone: string | null;
  onDial: (phoneNumber: string, isUrgent: boolean) => void;
  onClose: () => void;
}) {
  const handleSelect = (option: InquiryOption) => {
    customEvent({
      newGtm: { path: eventPath, trigger_and_target: option.trigger },
    });

    onClose();
    onDial(
      option.target === "facility" && facilityPhone
        ? facilityPhone
        : GOI_CALL_NUMBER,
      Boolean(option.urgent)
    );
  };

  return (
    <BaseStack width="100%" spacing={10} p="20px 24px 24px">
      {options.map((option) => (
        <BaseButton
          key={option.trigger}
          type="assistive_1"
          size="large"
          outlined
          onClick={() => handleSelect(option)}
        >
          <Box className="body_1_normal_sb" color={baseColors.gray_900}>
            {option.label}
          </Box>
        </BaseButton>
      ))}
    </BaseStack>
  );
}

/**
 * 화장장 화면의 전화 CTA 를 눌렀을 때 뜨는 문의 유형 선택 모달.
 *
 * 화장 예약·비용과 기타는 고이 상담으로, 개장·이장과 고인 확인·방문은
 * 해당 화장장으로 건다. 화장장 번호가 없는 시설은 고이로 폴백한다.
 *
 * 모달을 여는 버튼의 이벤트는 각 화면이 기존 이름 그대로 쏘고,
 * 여기서는 모달 안의 5개(선택 4 + 닫기)만 쏜다.
 *
 * @param eventPath 화면별 이벤트 path. 트리거 5종이 해당 path 에 등록돼 있어야 한다.
 * @param onDial 실제 발신 방식. platform 은 call(), parlor 는 raw tel: 을 쓴다.
 */
export const useInquiryModal = ({
  eventPath,
  options,
  onDial,
}: {
  eventPath: ClickEventType["path"];
  /** 버튼 구성. CREMATORIUM_INQUIRY_OPTIONS / PARLOR_INQUIRY_OPTIONS */
  options: InquiryOption[];
  onDial: (phoneNumber: string, isUrgent: boolean) => void;
}) => {
  const overlay = useOverlay();

  /** @param facilityPhone 화장장 대표번호. 없으면 null 을 넘기면 고이로 폴백한다. */
  const openInquiryModal = (facilityPhone: string | null) => {
    overlay.open(({ isOpen, close }) => {
      /** × 와 오버레이 클릭 양쪽이 같은 경로를 타도록 여기서 이벤트를 쏜다. */
      const closeWithEvent = () => {
        customEvent({
          newGtm: { path: eventPath, trigger_and_target: "ask-close" },
        });
        close();
      };

      return (
        <OverlayModal
          isOpen={isOpen}
          onClose={closeWithEvent}
          closeOnOverlayClick
          modalContentStyle={{ minWidth: "340px", borderRadius: "16px" }}
          header={{
            content: <InquiryModalHeader onClose={closeWithEvent} />,
          }}
          overlayModalContent={
            <InquiryModalBody
              eventPath={eventPath}
              options={options}
              facilityPhone={facilityPhone}
              onDial={onDial}
              onClose={close}
            />
          }
        />
      );
    });
  };

  return { openInquiryModal };
};

/** 화장장 상세용 프리셋. 기존 호출부 호환을 위해 시그니처를 유지한다. */
export const useCrematoriumInquiryModal = ({
  eventPath,
  onDial,
}: {
  eventPath: ClickEventType["path"];
  onDial: (phoneNumber: string, isUrgent: boolean) => void;
}) =>
  useInquiryModal({ eventPath, options: CREMATORIUM_INQUIRY_OPTIONS, onDial });

/** 장례식장용 프리셋. 조문 문의만 장례식장 직통으로 간다. */
export const useParlorInquiryModal = ({
  eventPath,
  onDial,
}: {
  eventPath: ClickEventType["path"];
  onDial: (phoneNumber: string, isUrgent: boolean) => void;
}) => useInquiryModal({ eventPath, options: PARLOR_INQUIRY_OPTIONS, onDial });
